import { useEffect, useRef, useState, useCallback } from 'react'
import { VStack, Text, Spinner, Banner } from 'wss3-forge'
import { useThemeMode } from '../contexts/ThemeContext'

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string
          callback: (token: string) => void
          'error-callback'?: () => void
          'expired-callback'?: () => void
          theme?: 'light' | 'dark' | 'auto'
          size?: 'normal' | 'compact'
          appearance?: 'always' | 'execute' | 'interaction-only'
        }
      ) => string
      remove: (widgetId: string) => void
      reset: (widgetId: string) => void
    }
  }
}

interface CaptchaChallengeProps {
  siteKey: string
  onVerify: (token: string) => void
  onError?: () => void
  onExpired?: () => void
  size?: 'normal' | 'compact'
}

export function CaptchaChallenge({
  siteKey,
  onVerify,
  onError,
  onExpired,
  size = 'normal'
}: CaptchaChallengeProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { mode } = useThemeMode()

  const loadTurnstileScript = useCallback(() => {
    return new Promise<void>((resolve, reject) => {
      // Check if already loaded
      if (window.turnstile) {
        resolve()
        return
      }

      // Check if script is already in DOM
      const existingScript = document.querySelector('script[src*="turnstile"]')
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve())
        return
      }

      // Load script
      const script = document.createElement('script')
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
      script.async = true
      script.defer = true

      script.onload = () => {
        // Wait a bit for turnstile to initialize
        setTimeout(() => resolve(), 100)
      }

      script.onerror = () => {
        reject(new Error('Failed to load Turnstile script'))
      }

      document.head.appendChild(script)
    })
  }, [])

  const renderWidget = useCallback(() => {
    if (!containerRef.current || !window.turnstile) return

    // Remove existing widget if any
    if (widgetIdRef.current) {
      try {
        window.turnstile.remove(widgetIdRef.current)
      } catch (e) {
        // Ignore
      }
    }

    try {
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token: string) => {
          onVerify(token)
        },
        'error-callback': () => {
          setError('Captcha verification failed. Please try again.')
          onError?.()
        },
        'expired-callback': () => {
          setError('Captcha expired. Please complete the challenge again.')
          onExpired?.()
        },
        theme: mode === 'dark' ? 'dark' : 'light',
        size,
        appearance: 'always'
      })
      setLoading(false)
    } catch (err) {
      console.error('Error rendering Turnstile widget:', err)
      setError('Failed to load captcha. Please refresh the page.')
      setLoading(false)
    }
  }, [siteKey, onVerify, onError, onExpired, mode, size])

  useEffect(() => {
    let mounted = true

    const init = async () => {
      try {
        await loadTurnstileScript()
        if (mounted) {
          renderWidget()
        }
      } catch (err: any) {
        if (mounted) {
          setError(err.message)
          setLoading(false)
        }
      }
    }

    init()

    return () => {
      mounted = false
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current)
        } catch (e) {
          // Ignore
        }
      }
    }
  }, [loadTurnstileScript, renderWidget])

  // Re-render when theme changes
  useEffect(() => {
    if (!loading && !error && window.turnstile && widgetIdRef.current) {
      renderWidget()
    }
  }, [mode, loading, error, renderWidget])

  if (error) {
    return (
      <Banner variant="error" title="Captcha Error">
        {error}
      </Banner>
    )
  }

  return (
    <VStack gap="sm" style={{ alignItems: 'center' }}>
      {loading && (
        <VStack gap="sm" style={{ padding: '1rem', alignItems: 'center' }}>
          <Spinner size="md" />
          <Text size="sm" color="muted">Loading security challenge...</Text>
        </VStack>
      )}
      <div
        ref={containerRef}
        style={{
          minHeight: loading ? 0 : 65,
          display: loading ? 'none' : 'block'
        }}
      />
    </VStack>
  )
}
